import React, { useState, useEffect, useCallback } from 'react';
import Icon from 'components/AppIcon';
import {
  getClients,
  getBillableTimeEntries,
  createInvoice,
  calculateVAT,
  calculateLineItemAmount,
  formatKES
} from '../../../services/invoiceService';

const ITEM_TYPES = [
  { value: 'time', label: 'Time Entry' },
  { value: 'fixed_fee', label: 'Fixed Fee' },
  { value: 'disbursement', label: 'Disbursement' }
];

const emptyLineItem = () => ({
  id: Date.now(),
  description: '',
  itemType: 'time',
  quantity: 1,
  rate: 0,
  timeEntryId: null
});

const CreateInvoiceModal = ({ onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [timeEntries, setTimeEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    clientId: '',
    caseTitle: '',
    description: '',
    notes: '',
    issueDate: new Date()?.toISOString()?.split('T')?.[0],
    dueDate: '',
    vatRate: 16
  });

  const [lineItems, setLineItems] = useState([emptyLineItem()]);

  const today = new Date();
  const defaultDue = new Date(today.setDate(today.getDate() + 30))?.toISOString()?.split('T')?.[0];

  useEffect(() => {
    setForm(f => ({ ...f, dueDate: defaultDue }));
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      const data = await getClients();
      setClients(data);
    } catch (err) {
      setError('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const loadTimeEntries = useCallback(async () => {
    try {
      const data = await getBillableTimeEntries();
      setTimeEntries(data);
    } catch (err) {
      console.error('Failed to load time entries');
    }
  }, []);

  useEffect(() => {
    if (step === 2) loadTimeEntries();
  }, [step, loadTimeEntries]);

  const updateLineItem = (id, field, value) => {
    setLineItems(items => items?.map(item =>
      item?.id === id ? { ...item, [field]: value } : item
    ));
  };

  const addLineItem = () => setLineItems(items => [...items, emptyLineItem()]);

  const removeLineItem = (id) => {
    if (lineItems?.length === 1) return;
    setLineItems(items => items?.filter(item => item?.id !== id));
  };

  const importTimeEntry = (entry) => {
    const hours = (entry?.durationMinutes || 0) / 60;
    const newItem = {
      id: Date.now(),
      description: entry?.activityDescription || 'Legal services',
      itemType: 'time',
      quantity: Math.round(hours * 100) / 100,
      rate: parseFloat(entry?.hourlyRate) || 0,
      timeEntryId: entry?.id
    };
    setLineItems(items => [...items?.filter(i => i?.description !== ''), newItem]);
  };

  const subtotal = lineItems?.reduce((sum, item) =>
    sum + calculateLineItemAmount(item?.quantity, item?.rate), 0
  );
  const { vatAmount, total } = calculateVAT(subtotal, form?.vatRate);

  const handleSubmit = async () => {
    if (!form?.clientId) { setError('Please select a client'); return; }
    if (!form?.dueDate) { setError('Please set a due date'); return; }
    if (lineItems?.every(i => !i?.description)) { setError('Add at least one line item'); return; }

    try {
      setSaving(true);
      setError(null);
      const validItems = lineItems?.filter(i => i?.description);
      await createInvoice({ ...form }, validItems);
      onCreated?.();
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to create invoice');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-surface rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div>
            <h2 className="text-lg font-heading-semibold text-text-primary">Create Invoice</h2>
            <p className="text-xs text-text-secondary mt-0.5">Step {step} of 3</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-secondary-50 transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-border bg-secondary-50">
          {['Invoice Details', 'Line Items', 'Review']?.map((label, i) => (
            <React.Fragment key={label}>
              <div className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-body-medium ${
                  step > i + 1 ? 'bg-primary text-white' :
                  step === i + 1 ? 'bg-primary text-white': 'bg-secondary-200 text-text-secondary'
                }`}>
                  {step > i + 1 ? <Icon name="Check" size={12} /> : i + 1}
                </div>
                <span className={`text-xs font-body-medium ${step === i + 1 ? 'text-primary' : 'text-text-secondary'}`}>
                  {label}
                </span>
              </div>
              {i < 2 && <div className="flex-1 h-px bg-border" />}
            </React.Fragment>
          ))}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">
          {error && (
            <div className="mb-4 flex items-center gap-2 bg-red-50 text-red-700 border border-red-200 px-4 py-3 rounded-lg text-sm">
              <Icon name="AlertCircle" size={16} />
              {error}
              <button onClick={() => setError(null)} className="ml-auto"><Icon name="X" size={14} /></button>
            </div>
          )}

          {/* Step 1: Invoice Details */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-1.5">Client *</label>
                  <select
                    value={form?.clientId}
                    onChange={e => setForm(f => ({ ...f, clientId: e?.target?.value }))}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                  >
                    <option value="">Select client...</option>
                    {clients?.map(c => (
                      <option key={c?.id} value={c?.id}>{c?.fullName} ({c?.email})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-1.5">Case / Matter Title *</label>
                  <input
                    type="text"
                    value={form?.caseTitle}
                    onChange={e => setForm(f => ({ ...f, caseTitle: e?.target?.value }))}
                    placeholder="e.g. Mwangi vs KRA - Tax Dispute"
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-1.5">Issue Date</label>
                  <input
                    type="date"
                    value={form?.issueDate}
                    onChange={e => setForm(f => ({ ...f, issueDate: e?.target?.value }))}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-1.5">Due Date *</label>
                  <input
                    type="date"
                    value={form?.dueDate}
                    onChange={e => setForm(f => ({ ...f, dueDate: e?.target?.value }))}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-1.5">VAT Rate (%)</label>
                  <input
                    type="number"
                    value={form?.vatRate}
                    onChange={e => setForm(f => ({ ...f, vatRate: parseFloat(e?.target?.value) || 16 }))}
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                  <p className="text-xs text-text-secondary mt-1">Kenya standard VAT rate is 16%</p>
                </div>
                <div>
                  <label className="block text-sm font-body-medium text-text-primary mb-1.5">Description</label>
                  <input
                    type="text"
                    value={form?.description}
                    onChange={e => setForm(f => ({ ...f, description: e?.target?.value }))}
                    placeholder="Brief invoice description"
                    className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-body-medium text-text-primary mb-1.5">Notes</label>
                <textarea
                  value={form?.notes}
                  onChange={e => setForm(f => ({ ...f, notes: e?.target?.value }))}
                  rows={3}
                  placeholder="Payment terms, bank details, or additional notes..."
                  className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-surface focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                />
              </div>
            </div>
          )}

          {/* Step 2: Line Items */}
          {step === 2 && (
            <div className="space-y-4">
              {/* Import from time entries */}
              {timeEntries?.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Icon name="Clock" size={16} className="text-blue-600" />
                    <span className="text-sm font-body-medium text-blue-700">Import from Time Entries</span>
                    <span className="text-xs text-blue-500">({timeEntries?.length} billable entries available)</span>
                  </div>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {timeEntries?.slice(0, 5)?.map(entry => (
                      <div key={entry?.id} className="flex items-center justify-between bg-white rounded-lg px-3 py-2 border border-blue-100">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-body-medium text-text-primary truncate">{entry?.activityDescription}</p>
                          <p className="text-xs text-text-secondary">
                            {((entry?.durationMinutes || 0) / 60)?.toFixed(2)} hrs × {formatKES(entry?.hourlyRate)}/hr
                          </p>
                        </div>
                        <button
                          onClick={() => importTimeEntry(entry)}
                          className="ml-3 text-xs text-blue-600 hover:text-blue-800 font-body-medium whitespace-nowrap"
                        >
                          + Import
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Line Items Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-body-medium text-text-primary">Line Items</h3>
                  <button
                    onClick={addLineItem}
                    className="flex items-center gap-1.5 text-xs text-primary hover:text-primary-700 font-body-medium"
                  >
                    <Icon name="Plus" size={14} />
                    Add Item
                  </button>
                </div>

                <div className="space-y-2">
                  {lineItems?.map((item, idx) => (
                    <div key={item?.id} className="grid grid-cols-12 gap-2 items-start bg-secondary-50 rounded-lg p-3">
                      <div className="col-span-5">
                        <input
                          type="text"
                          value={item?.description}
                          onChange={e => updateLineItem(item?.id, 'description', e?.target?.value)}
                          placeholder="Description"
                          className="w-full border border-border rounded-lg px-2 py-1.5 text-xs bg-surface focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div className="col-span-2">
                        <select
                          value={item?.itemType}
                          onChange={e => updateLineItem(item?.id, 'itemType', e?.target?.value)}
                          className="w-full border border-border rounded-lg px-2 py-1.5 text-xs bg-surface focus:outline-none focus:ring-1 focus:ring-primary"
                        >
                          {ITEM_TYPES?.map(t => <option key={t?.value} value={t?.value}>{t?.label}</option>)}
                        </select>
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          value={item?.quantity}
                          onChange={e => updateLineItem(item?.id, 'quantity', e?.target?.value)}
                          placeholder="Qty"
                          min="0"
                          step="0.01"
                          className="w-full border border-border rounded-lg px-2 py-1.5 text-xs bg-surface focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div className="col-span-2">
                        <input
                          type="number"
                          value={item?.rate}
                          onChange={e => updateLineItem(item?.id, 'rate', e?.target?.value)}
                          placeholder="Rate (KES)"
                          min="0"
                          step="0.01"
                          className="w-full border border-border rounded-lg px-2 py-1.5 text-xs bg-surface focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div className="col-span-1 flex items-center justify-end gap-1">
                        <span className="text-xs font-body-medium text-text-primary">
                          {formatKES(calculateLineItemAmount(item?.quantity, item?.rate))}
                        </span>
                        {lineItems?.length > 1 && (
                          <button onClick={() => removeLineItem(item?.id)} className="text-red-400 hover:text-red-600">
                            <Icon name="X" size={14} />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Totals */}
                <div className="mt-4 bg-secondary-50 rounded-xl p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">Subtotal</span>
                    <span className="font-body-medium">{formatKES(subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-text-secondary">VAT ({form?.vatRate}%)</span>
                    <span className="font-body-medium">{formatKES(vatAmount)}</span>
                  </div>
                  <div className="flex justify-between text-base font-heading-semibold border-t border-border pt-2">
                    <span>Total</span>
                    <span className="text-primary">{formatKES(total)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 3: Review */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="bg-secondary-50 rounded-xl p-4">
                <h3 className="text-sm font-heading-semibold text-text-primary mb-3">Invoice Summary</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-text-secondary">Client:</span>
                    <span className="ml-2 font-body-medium">{clients?.find(c => c?.id === form?.clientId)?.fullName || '—'}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Case:</span>
                    <span className="ml-2 font-body-medium">{form?.caseTitle || '—'}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Issue Date:</span>
                    <span className="ml-2 font-body-medium">{form?.issueDate}</span>
                  </div>
                  <div>
                    <span className="text-text-secondary">Due Date:</span>
                    <span className="ml-2 font-body-medium">{form?.dueDate}</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-heading-semibold text-text-primary mb-2">Line Items ({lineItems?.filter(i => i?.description)?.length})</h3>
                <div className="space-y-1">
                  {lineItems?.filter(i => i?.description)?.map(item => (
                    <div key={item?.id} className="flex justify-between text-sm py-1.5 border-b border-border">
                      <div>
                        <span className="font-body-medium text-text-primary">{item?.description}</span>
                        <span className="ml-2 text-xs text-text-secondary">{item?.quantity} × {formatKES(item?.rate)}</span>
                      </div>
                      <span className="font-body-medium">{formatKES(calculateLineItemAmount(item?.quantity, item?.rate))}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-primary-50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">Subtotal</span>
                  <span>{formatKES(subtotal)}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-text-secondary">VAT ({form?.vatRate}%)</span>
                  <span>{formatKES(vatAmount)}</span>
                </div>
                <div className="flex justify-between text-base font-heading-semibold border-t border-primary-200 pt-2">
                  <span>Total (KES)</span>
                  <span className="text-primary">{formatKES(total)}</span>
                </div>
              </div>

              {form?.notes && (
                <div className="bg-secondary-50 rounded-xl p-4">
                  <p className="text-xs text-text-secondary font-body-medium mb-1">Notes</p>
                  <p className="text-sm text-text-primary">{form?.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-border">
          <button
            onClick={() => step > 1 ? setStep(s => s - 1) : onClose()}
            className="flex items-center gap-2 px-4 py-2 text-sm font-body-medium text-text-secondary hover:text-text-primary transition-colors"
          >
            <Icon name="ChevronLeft" size={16} />
            {step === 1 ? 'Cancel' : 'Back'}
          </button>
          <div className="flex gap-3">
            {step < 3 ? (
              <button
                onClick={() => setStep(s => s + 1)}
                disabled={step === 1 && !form?.clientId}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg text-sm font-body-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <Icon name="ChevronRight" size={16} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2 bg-primary text-white rounded-lg text-sm font-body-medium hover:bg-primary-700 transition-colors disabled:opacity-50"
              >
                {saving ? <Icon name="Loader" size={16} className="animate-spin" /> : <Icon name="Check" size={16} />}
                {saving ? 'Creating...' : 'Create Invoice'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateInvoiceModal;
